import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Marker, Pane, Tooltip, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import {
  buildApiFetchOptions,
  buildAreaArtifactUrl,
  buildAreaDataUrl,
  buildAreaElectionsUrl,
  computeAutoBins,
  formatBinRange,
  formatInt,
  formatPct,
  getBinIndex,
  getPaletteSteps,
  getPartiesFromResults,
  getPrecinctLabel,
  getResultDisplayName,
  getStatsForOkrsek,
  getSubjectLabel,
  MapPane,
  useElectionData,
} from "./Map";
import { BRAND_COLORS, BRAND_MAP_COLOR_PRESETS, normalizeHexColor } from "./branding";
import { getElectionLabel, sortElectionsNewestFirst } from "./electionOrdering";
import {
  getStoredChoiceForElection,
  pickValidChoice,
  readElectionSelectionContext,
  setStoredChoiceForElection,
  writeElectionSelectionContext,
} from "./electionSelectionContext";
import {
  clearStoredViewport,
  getInitialMapView,
  makeAutoFitRequestKey,
  readStoredViewport,
  writeStoredViewport,
} from "./mapViewport";
import { ELECTION_MAP_STANDARD_V1, ELECTION_MAP_STANDARD_V1_KEY, getElectionMapFeatureStyle } from "./mapRenderingStandard";
import {
  buildAreaGeometryIndex,
  filterMhdStops,
  getAddressPointsForFeature,
  getAddressSummaryForFeature,
  getFeatureRepresentativePoint,
  getGeoJsonBounds,
  groupAddressPointsForDisplay,
  LANDMARK_CATEGORIES,
  LANDMARK_MHD_COLORS,
  LANDMARK_MHD_TYPE_LABELS,
  LANDMARK_MHD_TYPE_MARKERS,
  LANDMARK_MHD_TYPES,
  normalizeAddressEnrichmentIndex,
  normalizeAddressEnrichmentPoints,
  normalizeMhdStopsPayload,
} from "./landmarks";
import AddToReportButton from "./reports/components/AddToReportButton";
import { buildDefaultReportTitle, captureLeafletViewport, mapLegendToSnapshot } from "./reports/capture";
import { REPORT_ITEM_TYPES, REPORT_ORIENTATIONS, REPORT_SOURCE_MODULES } from "./reports/reportTypes";

const EMPTY_GEOJSON = { type: "FeatureCollection", features: [] };
const NO_DATA_FILL_COLOR = "#e2e8f0";
const NEUTRAL_ELECTION_ID = "__none__";
const NEUTRAL_ELECTION_LABEL = "==";
const NEUTRAL_FILL_COLOR = "#ffffff";
const NEUTRAL_SELECTED_FILL_COLOR = "#e0f2fe";
const NEUTRAL_PSP2025_PREFIX = "psp2025";
const MARKER_ZOOM_THRESHOLD = 12;
const MAX_VISIBLE_MARKERS = 900;
const MAX_REPORT_VISIBLE_MARKERS = 80;
const MHD_MARKER_PANE = "volby-landmarks-mhd-markers";
const MHD_TOOLTIP_PANE = "volby-landmarks-mhd-tooltips";
const PRECINCT_LABEL_PANE = "volby-landmarks-precinct-labels";
const LANDMARK_STORAGE_KEY = "electionmap.landmarks.v1";
const LANDMARK_SETTINGS_VERSION = 4;
const VISIBLE_LANDMARK_CATEGORIES = LANDMARK_CATEGORIES.filter((category) => ["mhd", "billboards"].includes(category.key));
const MARKER_COLOR_OPTIONS = [
  { name: "Tmavě modrá", hex: BRAND_COLORS.primaryDarkBlue },
  ...BRAND_MAP_COLOR_PRESETS,
].filter((item, index, arr) => arr.findIndex((candidate) => candidate.hex === item.hex) === index);
const DEFAULT_SCALE_COLOR = BRAND_MAP_COLOR_PRESETS[1]?.hex || BRAND_COLORS.primaryBrightBlue;

function createHttpError(status, label) {
  const error = new Error(`${label} HTTP ${status}`);
  error.status = Number(status);
  return error;
}

function readStoredLandmarkSettings() {
  if (typeof window === "undefined" || !window.localStorage) return {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem(LANDMARK_STORAGE_KEY) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (_) {
    return {};
  }
}

function writeStoredLandmarkSettings(settings) {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(LANDMARK_STORAGE_KEY, JSON.stringify(settings && typeof settings === "object" ? settings : {}));
  } catch (_) {
    // Local module preferences are best-effort only.
  }
}

function getColorPopoverPosition(anchorElement) {
  if (!anchorElement || typeof anchorElement.getBoundingClientRect !== "function") {
    return { top: 96, left: 24 };
  }
  const rect = anchorElement.getBoundingClientRect();
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1280;
  const popoverWidth = 236;
  const left = Math.max(12, Math.min(rect.right - popoverWidth, viewportWidth - popoverWidth - 12));
  return {
    top: Math.max(12, rect.bottom + 8),
    left,
  };
}

function LandmarksColorPresetPicker({
  pickerKey,
  label,
  value,
  colorPresets,
  openPicker,
  setOpenPicker,
  onChange,
}) {
  const triggerRef = useRef(null);
  const activeColor = normalizeHexColor(value) || BRAND_COLORS.primaryBrightBlue;
  const isOpen = openPicker?.key === pickerKey;

  function openPalette() {
    setOpenPicker({
      key: pickerKey,
      ...getColorPopoverPosition(triggerRef.current),
    });
  }

  function closePalette() {
    setOpenPicker((current) => (current?.key === pickerKey ? null : current));
  }

  return (
    <div className="landmarks-color-picker">
      <button
        ref={triggerRef}
        type="button"
        className="landmarks-color-trigger"
        aria-label={`Upravit barvu: ${label}`}
        aria-expanded={isOpen}
        onClick={openPalette}
      >
        <span className="landmarks-color-button-swatch" style={{ background: activeColor }} aria-hidden="true" />
      </button>
      {isOpen ? (
        <div
          className="landmarks-color-menu"
          style={{ top: openPicker.top, left: openPicker.left }}
          role="dialog"
          aria-label={`Vyberte barvu pro ${label}`}
        >
          <div className="landmarks-color-menu-header">
            <div className="landmarks-color-menu-title">Vyberte barvu</div>
            <button
              type="button"
              className="landmarks-color-close"
              aria-label="Zavřít výběr barvy"
              onClick={closePalette}
            >
              ×
            </button>
          </div>
          <div className="landmarks-color-grid">
            {colorPresets.map((preset) => {
              const color = normalizeHexColor(preset.hex);
              if (!color) return null;
              const isActive = color === activeColor;
              return (
                <button
                  key={`${pickerKey}-${color}`}
                  type="button"
                  className={`landmarks-color-preset${isActive ? " is-active" : ""}`}
                  style={{ background: color }}
                  aria-label={`${preset.name}: ${label}`}
                  aria-pressed={isActive}
                  title={preset.name}
                  onClick={() => onChange(color)}
                />
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function getFeaturePrecinctId(feature) {
  return String(feature?.properties?.okrsek || feature?.properties?.district_code || feature?.properties?.okrsek_code || "").trim();
}

function isSenElectionId(electionId) {
  return String(electionId || "").toLowerCase().startsWith("sen");
}

function getElectionId(election) {
  return String(election?.id || election?.election_id || "").trim();
}

function isNeutralElectionId(value) {
  return String(value || "") === NEUTRAL_ELECTION_ID;
}

function isPsp2025ElectionId(value) {
  return String(value || "").toLowerCase().startsWith(NEUTRAL_PSP2025_PREFIX);
}

function getMunicipalityName(properties) {
  const districtName = String(properties?.naz_mco || "").trim();
  const municipalityName = String(properties?.naz_obec || "").trim();
  return districtName || municipalityName || "";
}

function getFeaturePrecinctLabel(feature, electionId) {
  const properties = feature?.properties || {};
  return getPrecinctLabel(
    {
      raw: getFeaturePrecinctId(feature),
      areaCsuCode: properties.area_csu_code,
      municipalityName: getMunicipalityName(properties),
    },
    electionId,
    null
  );
}

function buildResultsByPrecinct(results) {
  const next = new Map();
  for (const row of Array.isArray(results) ? results : []) {
    const precinctId = String(row?.okrsek || "").trim();
    if (precinctId) next.set(precinctId, Array.isArray(row.results) ? row.results : []);
  }
  return next;
}

function buildStatsByPrecinct(stats) {
  const next = new Map();
  for (const row of Array.isArray(stats) ? stats : []) {
    const precinctId = String(row?.okrsek || "").trim();
    if (precinctId) next.set(precinctId, row);
  }
  return next;
}

function getStatsVoters(statsByPrecinct, precinctId, electionId) {
  const statsInfo = getStatsForOkrsek(statsByPrecinct, precinctId, electionId);
  const voters = Number(statsInfo?.voters);
  return Number.isFinite(voters) ? voters : null;
}

function comparePrecinctCodes(a, b) {
  return String(a || "").localeCompare(String(b || ""), "cs-CZ", { numeric: true, sensitivity: "base" });
}

function getAddressSummaryForSelectedPrecinct(index, row) {
  const directSummary = getAddressSummaryForFeature(index, row?.feature);
  if (directSummary || !index?.available || !(index.precincts instanceof Map)) return directSummary;

  const precinctCode = String(row?.precinctId || "").trim();
  if (!precinctCode) return null;

  const matches = [];
  for (const [precinctId, summary] of index.precincts.entries()) {
    if (String(summary?.okrsekCode || "").trim() === precinctCode) {
      matches.push(String(precinctId || ""));
    }
  }
  if (matches.length !== 1) return null;

  const separatorIndex = matches[0].indexOf(":");
  if (separatorIndex <= 0 || separatorIndex === matches[0].length - 1) return null;

  const areaCsuCode = matches[0].slice(0, separatorIndex).trim();
  const okrsekCode = matches[0].slice(separatorIndex + 1).trim();
  if (!areaCsuCode || !okrsekCode) return null;

  return getAddressSummaryForFeature(index, {
    ...(row?.feature || {}),
    properties: {
      ...(row?.feature?.properties || {}),
      area_csu_code: areaCsuCode,
      okrsek_code: okrsekCode,
    },
  });
}

function getAddressPointsForSelectedPrecinct(pointsIndex, row, addressIndex) {
  return getAddressPointsForFeature(pointsIndex, row?.feature, addressIndex);
}

function getEntityValue(resultsByPrecinct, precinctId, selectedEntity) {
  const rows = resultsByPrecinct.get(String(precinctId)) || [];
  const hit = rows.find((row) => getResultDisplayName(row) === selectedEntity);
  return {
    percentage: Number.isFinite(Number(hit?.percentage)) ? Number(hit.percentage) : null,
    votes: Number.isFinite(Number(hit?.votes)) ? Number(hit.votes) : null,
  };
}

function createPrecinctLabelIcon(label) {
  const text = String(label || "").replace(/[&<>"']/g, (char) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char]
  ));
  return L.divIcon({
    className: "landmarks-precinct-label-icon",
    html: `<span class="landmarks-precinct-label">${text}</span>`,
    iconSize: [58, 20],
    iconAnchor: [29, 10],
  });
}

function viewportToBounds(bounds) {
  if (!bounds || typeof bounds.getSouth !== "function") return null;
  return {
    minLat: bounds.getSouth(),
    minLng: bounds.getWest(),
    maxLat: bounds.getNorth(),
    maxLng: bounds.getEast(),
  };
}

function MarkerViewport({ onViewportChange }) {
  const map = useMap();
  const update = useCallback(() => {
    try {
      onViewportChange({
        bounds: viewportToBounds(map.getBounds()),
        zoom: map.getZoom(),
      });
    } catch (_) {}
  }, [map, onViewportChange]);

  useEffect(() => {
    update();
  }, [update]);

  useMapEvents({
    moveend: update,
    zoomend: update,
  });

  return null;
}

function createLandmarkIcon(marker, markerColors) {
  const color =
    normalizeHexColor(markerColors?.[marker.type]) ||
    normalizeHexColor(LANDMARK_MHD_COLORS[marker.type]) ||
    BRAND_COLORS.primaryBrightBlue;
  const label = LANDMARK_MHD_TYPE_MARKERS[marker.type] || LANDMARK_MHD_TYPE_LABELS[marker.type] || marker.type;
  const isMetro = marker.type === "METRO";
  const width = isMetro ? 26 : 18;
  const height = isMetro ? 16 : 16;
  return L.divIcon({
    className: "landmarks-div-icon",
    html: `<span class="landmarks-marker-label" style="--landmark-marker-color:${color}">${label}</span>`,
    iconSize: [width, height],
    iconAnchor: [width / 2, height / 2],
    tooltipAnchor: [0, -height / 2],
  });
}

function LandmarkMarkersLayer({ markers, markerColors, onViewportChange }) {
  const activeTooltipMarkerRef = useRef(null);

  const closeActiveTooltip = useCallback(() => {
    const marker = activeTooltipMarkerRef.current;
    if (!marker) return;
    try {
      marker.closeTooltip();
    } catch (_) {}
    activeTooltipMarkerRef.current = null;
  }, []);

  const handleViewportChange = useCallback(
    (viewport) => {
      closeActiveTooltip();
      onViewportChange(viewport);
    },
    [closeActiveTooltip, onViewportChange]
  );

  return (
    <>
      <Pane name={MHD_TOOLTIP_PANE} style={{ zIndex: ELECTION_MAP_STANDARD_V1.paneZIndexes.markers + 180, pointerEvents: "none" }} />
      <Pane name={MHD_MARKER_PANE} style={{ zIndex: ELECTION_MAP_STANDARD_V1.paneZIndexes.markers + 20 }}>
        <MarkerViewport onViewportChange={handleViewportChange} />
        {markers.map((marker) => {
          const routeLabel = marker.routes.length ? marker.routes.slice(0, 12).join(", ") : "linky neuvedeny";
          return (
            <Marker
              key={marker.id}
              position={[marker.lat, marker.lng]}
              icon={createLandmarkIcon(marker, markerColors)}
              eventHandlers={{
                mouseover: (event) => {
                  const target = event?.target;
                  if (!target) return;
                  if (activeTooltipMarkerRef.current && activeTooltipMarkerRef.current !== target) {
                    try {
                      activeTooltipMarkerRef.current.closeTooltip();
                    } catch (_) {}
                  }
                  activeTooltipMarkerRef.current = target;
                  try {
                    target.openTooltip();
                  } catch (_) {}
                },
                mouseout: (event) => {
                  const target = event?.target;
                  if (activeTooltipMarkerRef.current === target) activeTooltipMarkerRef.current = null;
                  try {
                    target?.closeTooltip();
                  } catch (_) {}
                },
                remove: (event) => {
                  if (activeTooltipMarkerRef.current === event?.target) activeTooltipMarkerRef.current = null;
                },
              }}
            >
              <Tooltip
                direction="top"
                offset={[0, -30]}
                opacity={0.97}
                className="landmarks-marker-tooltip"
                pane={MHD_TOOLTIP_PANE}
                interactive={false}
                permanent={false}
                sticky={false}
              >
                <strong>{marker.stopName}</strong>
                <br />
                <span className="landmarks-marker-tooltip-lines">{routeLabel}</span>
              </Tooltip>
            </Marker>
          );
        })}
      </Pane>
    </>
  );
}

function PrecinctNumberLabelsLayer({ labels }) {
  if (!Array.isArray(labels) || labels.length === 0) return null;

  return (
    <Pane name={PRECINCT_LABEL_PANE} style={{ zIndex: ELECTION_MAP_STANDARD_V1.paneZIndexes.labels + 12, pointerEvents: "none" }}>
      {labels.map((label) => (
        <Marker
          key={label.precinctId}
          position={[label.lat, label.lng]}
          icon={createPrecinctLabelIcon(label.text)}
          interactive={false}
          keyboard={false}
        />
      ))}
    </Pane>
  );
}

export default function LandmarksPage({ areaId, apiBase = "/api", enableReports = true }) {
  const storedSettings = useMemo(() => readStoredLandmarkSettings(), []);
  const storedVersion = Number(storedSettings.version) === LANDMARK_SETTINGS_VERSION ? storedSettings : {};
  const [elections, setElections] = useState([]);
  const [electionsError, setElectionsError] = useState("");
  const [isLoadingElections, setIsLoadingElections] = useState(false);
  const [electionId, setElectionId] = useState("");
  const [selectedEntity, setSelectedEntity] = useState("");
  const [enabledCategories, setEnabledCategories] = useState(() => ({
    mhd: Boolean(storedVersion.enabledCategories?.mhd),
    parks: false,
    playgrounds: false,
    billboards: Boolean(storedVersion.enabledCategories?.billboards),
  }));
  const [enabledMhdTypes, setEnabledMhdTypes] = useState(() => ({
    METRO: storedVersion.enabledMhdTypes?.METRO !== false,
    TRAIN: storedVersion.enabledMhdTypes?.TRAIN !== false,
    TRAM: Boolean(storedVersion.enabledMhdTypes?.TRAM),
    BUS: Boolean(storedVersion.enabledMhdTypes?.BUS),
  }));
  const [markerColors, setMarkerColors] = useState(() => ({
    ...LANDMARK_MHD_COLORS,
    ...(storedVersion.markerColors && typeof storedVersion.markerColors === "object" ? storedVersion.markerColors : {}),
  }));
  const [baseColor, setBaseColor] = useState(() => normalizeHexColor(storedVersion.baseColor) || DEFAULT_SCALE_COLOR);
  const [openPicker, setOpenPicker] = useState(null);
  const [mhdStops, setMhdStops] = useState([]);
  const [mhdError, setMhdError] = useState("");
  const [isLoadingMhd, setIsLoadingMhd] = useState(false);
  const [billboardError, setBillboardError] = useState("");
  const [isLoadingBillboards, setIsLoadingBillboards] = useState(false);
  const [areaBoundaryGeoJson, setAreaBoundaryGeoJson] = useState(EMPTY_GEOJSON);
  const [areaBoundaryError, setAreaBoundaryError] = useState("");
  const [showAddressSummary, setShowAddressSummary] = useState(true);
  const [addressIndexState, setAddressIndexState] = useState({ cacheKey: "", index: null, isLoading: false });
  const [addressPointsState, setAddressPointsState] = useState({ cacheKey: "", points: null, isLoading: false });
  const [hoveredPrecinct, setHoveredPrecinct] = useState(null);
  const [selectedPrecinctIds, setSelectedPrecinctIds] = useState(() => new Set());
  const [tooltip, setTooltip] = useState(null);
  const [markerViewport, setMarkerViewport] = useState({ bounds: null, zoom: 0 });
  const mapRef = useRef(null);
  const layerIndexRef = useRef(new Map());
  const addressIndexCacheRef = useRef(new Map());
  const addressPointsCacheRef = useRef(new Map());
  const initialViewport = useMemo(() => getInitialMapView("landmarks", areaId), [areaId]);
  const hasStoredInitialViewportRef = useRef(Boolean(readStoredViewport("landmarks", areaId)));
  const [fitRequestKey, setFitRequestKey] = useState(() =>
    hasStoredInitialViewportRef.current ? "" : makeAutoFitRequestKey("landmarks", areaId, "initial")
  );
  const prevAreaIdRef = useRef(areaId);

  useEffect(() => {
    writeStoredLandmarkSettings({
      version: LANDMARK_SETTINGS_VERSION,
      enabledCategories,
      enabledMhdTypes,
      markerColors,
      baseColor,
    });
  }, [baseColor, enabledCategories, enabledMhdTypes, markerColors]);

  useEffect(() => {
    if (!areaId) {
      setElections([]);
      setElectionId("");
      return undefined;
    }

    const ac = new AbortController();
    setIsLoadingElections(true);
    setElectionsError("");
    setElections([]);
    setElectionId(NEUTRAL_ELECTION_ID);

    (async () => {
      try {
        const response = await fetch(buildAreaElectionsUrl(areaId, apiBase), buildApiFetchOptions(apiBase, { signal: ac.signal }));
        if (!response.ok) throw createHttpError(response.status, "enabled elections");
        const payload = await response.json();
        if (ac.signal.aborted) return;
        const nextElections = sortElectionsNewestFirst(Array.isArray(payload) ? payload : []);
        setElections(nextElections);
        setElectionId(NEUTRAL_ELECTION_ID);
      } catch (err) {
        if (err?.name === "AbortError") return;
        setElectionsError(Number(err?.status) === 403 ? "Tato oblast není pro váš účet dostupná." : "Nepodařilo se načíst povolené volby.");
      } finally {
        if (!ac.signal.aborted) setIsLoadingElections(false);
      }
    })();

    return () => ac.abort();
  }, [areaId, apiBase]);

  useEffect(() => {
    if (!enabledCategories.mhd) {
      setMhdStops([]);
      setMhdError("");
      setIsLoadingMhd(false);
      return undefined;
    }

    const ac = new AbortController();
    setIsLoadingMhd(true);
    setMhdError("");
    fetch("/landmarks/mhd_stops.json", { signal: ac.signal, cache: "no-store" })
      .then((response) => {
        if (!response.ok) throw createHttpError(response.status, "mhd landmarks");
        return response.json();
      })
      .then((payload) => {
        if (ac.signal.aborted) return;
        setMhdStops(normalizeMhdStopsPayload(payload));
      })
      .catch((err) => {
        if (err?.name === "AbortError") return;
        setMhdStops([]);
        setMhdError("MHD artifact není k dispozici.");
      })
      .finally(() => {
        if (!ac.signal.aborted) setIsLoadingMhd(false);
      });
    return () => ac.abort();
  }, [enabledCategories.mhd]);

  useEffect(() => {
    if (!enabledCategories.billboards) {
      setBillboardError("");
      setIsLoadingBillboards(false);
      return undefined;
    }

    const ac = new AbortController();
    setIsLoadingBillboards(true);
    setBillboardError("");
    fetch("/landmarks/billboards.json", { signal: ac.signal, cache: "no-store" })
      .then((response) => {
        if (!response.ok) throw createHttpError(response.status, "billboard landmarks");
        return response.json();
      })
      .catch((err) => {
        if (err?.name === "AbortError") return;
        setBillboardError("Billboard artifact není k dispozici.");
      })
      .finally(() => {
        if (!ac.signal.aborted) setIsLoadingBillboards(false);
      });
    return () => ac.abort();
  }, [enabledCategories.billboards]);

  const isNeutralMode = isNeutralElectionId(electionId);
  const psp2025ElectionId = useMemo(
    () => getElectionId(elections.find((election) => isPsp2025ElectionId(getElectionId(election)))),
    [elections]
  );
  const neutralGeometryElectionId = useMemo(() => {
    if (psp2025ElectionId) return psp2025ElectionId;
    const nonSenElection = elections.find((election) => {
      const candidateId = getElectionId(election);
      return candidateId && !isSenElectionId(candidateId);
    });
    return getElectionId(nonSenElection) || getElectionId(elections[0]) || "";
  }, [elections, psp2025ElectionId]);
  const dataElectionId = isNeutralMode ? neutralGeometryElectionId : electionId;
  const hasNeutralPsp2025Stats = isNeutralMode && dataElectionId && dataElectionId === psp2025ElectionId;

  const areaBoundaryElectionId = useMemo(() => {
    if (dataElectionId && !isSenElectionId(dataElectionId)) return dataElectionId;
    const nonSenElection = elections.find((election) => {
      const candidateId = getElectionId(election);
      return candidateId && !isSenElectionId(candidateId);
    });
    return getElectionId(nonSenElection) || dataElectionId;
  }, [dataElectionId, elections]);

  useEffect(() => {
    if (!areaId || !areaBoundaryElectionId || areaBoundaryElectionId === dataElectionId) {
      setAreaBoundaryGeoJson(EMPTY_GEOJSON);
      setAreaBoundaryError("");
      return undefined;
    }

    const ac = new AbortController();
    setAreaBoundaryError("");
    setAreaBoundaryGeoJson(EMPTY_GEOJSON);

    (async () => {
      try {
        const url = buildAreaDataUrl("districts/geojson", areaId, areaBoundaryElectionId, apiBase);
        const response = await fetch(url, buildApiFetchOptions(apiBase, { signal: ac.signal }));
        if (!response.ok) throw createHttpError(response.status, "area boundary districts");
        const payload = await response.json();
        if (ac.signal.aborted) return;
        const ok = payload && payload.type === "FeatureCollection" && Array.isArray(payload.features);
        setAreaBoundaryGeoJson(ok ? payload : EMPTY_GEOJSON);
      } catch (err) {
        if (err?.name === "AbortError") return;
        setAreaBoundaryGeoJson(EMPTY_GEOJSON);
        setAreaBoundaryError("Nepodařilo se načíst geometrii oblasti pro filtrování landmarků.");
      }
    })();

    return () => ac.abort();
  }, [areaBoundaryElectionId, areaId, dataElectionId, apiBase]);

  useEffect(() => {
    if (prevAreaIdRef.current === areaId) return;
    prevAreaIdRef.current = areaId;
    setSelectedPrecinctIds(new Set());
    clearStoredViewport("landmarks", areaId);
    setFitRequestKey(makeAutoFitRequestKey("landmarks", areaId, "area"));
  }, [areaId]);

  useEffect(() => {
    if (!isNeutralMode) setSelectedPrecinctIds(new Set());
  }, [isNeutralMode]);

  useEffect(() => {
    layerIndexRef.current = new Map();
    setHoveredPrecinct(null);
    setTooltip(null);
    if (dataElectionId) setFitRequestKey(makeAutoFitRequestKey("landmarks", areaId, `election-${dataElectionId}`));
  }, [areaId, dataElectionId]);

  const data = useElectionData(dataElectionId, areaId, apiBase);
  const features = useMemo(() => (Array.isArray(data.districtsGeoJson?.features) ? data.districtsGeoJson.features : []), [data.districtsGeoJson]);
  const featureCount = features.length;
  const subjectLabel = getSubjectLabel(electionId);
  const parties = useMemo(() => getPartiesFromResults(data.results), [data.results]);
  const resultsByPrecinct = useMemo(() => buildResultsByPrecinct(data.results), [data.results]);
  const statsByPrecinct = useMemo(() => buildStatsByPrecinct(data.stats), [data.stats]);
  const boundaryGeoJson = areaBoundaryElectionId === dataElectionId ? data.districtsGeoJson : areaBoundaryGeoJson;
  const geoJsonBounds = useMemo(() => getGeoJsonBounds(boundaryGeoJson), [boundaryGeoJson]);
  const areaGeometryIndex = useMemo(() => buildAreaGeometryIndex(boundaryGeoJson), [boundaryGeoJson]);
  const paletteSteps = useMemo(() => getPaletteSteps(baseColor), [baseColor]);

  useEffect(() => {
    if (!areaId || !electionId || isNeutralMode) return;
    writeElectionSelectionContext(areaId, {
      landmarksElectionId: electionId,
      landmarksEntityByElection: setStoredChoiceForElection(
        readElectionSelectionContext(areaId),
        "landmarksEntityByElection",
        electionId,
        selectedEntity
      ),
      lastElectionId: electionId,
    });
  }, [areaId, electionId, isNeutralMode, selectedEntity]);

  useEffect(() => {
    if (isNeutralMode) {
      if (selectedEntity) setSelectedEntity("");
      return;
    }
    const storedSelection = readElectionSelectionContext(areaId);
    const next = pickValidChoice(
      parties,
      [getStoredChoiceForElection(storedSelection, "landmarksEntityByElection", electionId), selectedEntity],
      parties[0] || ""
    );
    if (next !== selectedEntity) setSelectedEntity(next);
  }, [areaId, electionId, isNeutralMode, parties, selectedEntity]);

  const valuesPct = useMemo(() => {
    if (isNeutralMode) return [];
    const out = [];
    for (const feature of features) {
      const precinctId = getFeaturePrecinctId(feature);
      const value = getEntityValue(resultsByPrecinct, precinctId, selectedEntity).percentage;
      if (Number.isFinite(value)) out.push(value);
    }
    return out;
  }, [features, isNeutralMode, resultsByPrecinct, selectedEntity]);
  const bins = useMemo(() => computeAutoBins(valuesPct, 4), [valuesPct]);
  const landmarkLegendItems = useMemo(() => {
    const items = [];
    for (let index = 0; index < bins.length - 1; index += 1) {
      items.push({
        label: formatBinRange(bins[index], bins[index + 1]),
        color: paletteSteps[Math.min(index, paletteSteps.length - 1)] || DEFAULT_SCALE_COLOR,
      });
    }
    items.push({ label: "bez dat pro výběr", color: NO_DATA_FILL_COLOR });
    return items;
  }, [bins, paletteSteps]);

  const fillColorForPct = useCallback(
    (pct) => {
      const idx = getBinIndex(pct, bins);
      if (idx < 0) return NO_DATA_FILL_COLOR;
      return paletteSteps[Math.min(idx, paletteSteps.length - 1)] || paletteSteps[paletteSteps.length - 1];
    },
    [bins, paletteSteps]
  );
  const electionLabel = getElectionLabel(elections.find((item) => String(item.id) === String(electionId)) || { id: electionId });
  const buildLandmarksReportSnapshot = useCallback(
    () => ({
      kind: REPORT_ITEM_TYPES.LANDMARKS_MAP,
      sourceModule: REPORT_SOURCE_MODULES.LANDMARKS,
      areaId,
      electionId,
      electionLabel,
      selectedEntity,
      partyLabel: selectedEntity,
      enabledLayers: { ...enabledCategories },
      enabledMhdTypes: { ...enabledMhdTypes },
      colors: {
        baseColor,
        markerColors: { ...markerColors },
      },
      colorState: {
        baseColor,
        bins,
      },
      legend: mapLegendToSnapshot(landmarkLegendItems),
      viewport: captureLeafletViewport(mapRef.current) || initialViewport,
      markerViewport,
      capturedAt: new Date().toISOString(),
    }),
    [
      areaId,
      baseColor,
      bins,
      electionId,
      electionLabel,
      enabledCategories,
      enabledMhdTypes,
      initialViewport,
      landmarkLegendItems,
      markerColors,
      markerViewport,
      selectedEntity,
    ]
  );

  const styleFeature = useCallback(
    (feature) => {
      const precinctId = getFeaturePrecinctId(feature);
      const isHovered = hoveredPrecinct != null && String(hoveredPrecinct) === precinctId;
      const isSelected = isNeutralMode && selectedPrecinctIds.has(precinctId);
      if (isNeutralMode) {
        const style = getElectionMapFeatureStyle({
          fillColor: isSelected ? NEUTRAL_SELECTED_FILL_COLOR : NEUTRAL_FILL_COLOR,
          noDataFillColor: NEUTRAL_FILL_COLOR,
          isHovered,
          isSelected,
          clearOnFocus: false,
        });
        return {
          ...style,
          color: isSelected ? "#2563eb" : isHovered ? "#334155" : "#64748b",
          weight: isSelected ? 3.2 : isHovered ? 2.4 : 1.15,
          opacity: isSelected || isHovered ? 0.98 : 0.74,
          fillOpacity: isSelected ? 0.18 : isHovered ? 0.06 : 0.025,
        };
      }
      const { percentage } = getEntityValue(resultsByPrecinct, precinctId, selectedEntity);
      return getElectionMapFeatureStyle({
        fillColor: fillColorForPct(percentage),
        noDataFillColor: NO_DATA_FILL_COLOR,
        isHovered,
        isSelected: false,
        clearOnFocus: true,
      });
    },
    [fillColorForPct, hoveredPrecinct, isNeutralMode, resultsByPrecinct, selectedEntity, selectedPrecinctIds]
  );

  useEffect(() => {
    for (const layer of layerIndexRef.current.values()) {
      try {
        layer.setStyle(styleFeature(layer.feature));
      } catch (_) {}
    }
  }, [styleFeature]);

  const showTooltipAtEvent = useCallback((precinctId, event) => {
    const originalEvent = event?.originalEvent || event;
    const clientX = Number(originalEvent?.clientX);
    const clientY = Number(originalEvent?.clientY);
    if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) return;
    const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1440;
    const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 900;
    setTooltip({
      precinctId: String(precinctId || ""),
      x: Math.max(12, Math.min(clientX + 18, viewportWidth - 330)),
      y: Math.max(12, Math.min(clientY + 18, viewportHeight - 190)),
    });
  }, []);

  const onEachFeature = useCallback(
    (feature, layer) => {
      const precinctId = getFeaturePrecinctId(feature);
      if (!precinctId) return;
      layerIndexRef.current.set(precinctId, layer);
      layer.on({
        mouseover: (event) => {
          setHoveredPrecinct(precinctId);
          showTooltipAtEvent(precinctId, event);
        },
        mousemove: (event) => showTooltipAtEvent(precinctId, event),
        mouseout: () => {
          setHoveredPrecinct((current) => (String(current || "") === precinctId ? null : current));
          setTooltip(null);
        },
        click: () => {
          if (!isNeutralMode) return;
          setSelectedPrecinctIds((current) => {
            const next = new Set(current);
            if (next.has(precinctId)) next.delete(precinctId);
            else next.add(precinctId);
            return next;
          });
        },
      });
    },
    [isNeutralMode, showTooltipAtEvent]
  );

  const tooltipRow = useMemo(() => {
    if (!tooltip?.precinctId) return null;
    const feature = features.find((item) => getFeaturePrecinctId(item) === tooltip.precinctId);
    if (isNeutralMode) {
      const voters = hasNeutralPsp2025Stats ? getStatsVoters(statsByPrecinct, tooltip.precinctId, psp2025ElectionId) : null;
      return {
        precinctLabel: `Okrsek č. ${tooltip.precinctId}`,
        voters2025: voters,
      };
    }
    const value = getEntityValue(resultsByPrecinct, tooltip.precinctId, selectedEntity);
    return {
      precinctLabel: feature ? getFeaturePrecinctLabel(feature, electionId) : tooltip.precinctId,
      votes: value.votes,
      percentage: value.percentage,
      statsInfo: getStatsForOkrsek(statsByPrecinct, tooltip.precinctId, electionId),
    };
  }, [electionId, features, hasNeutralPsp2025Stats, isNeutralMode, psp2025ElectionId, resultsByPrecinct, selectedEntity, statsByPrecinct, tooltip]);

  const precinctLabels = useMemo(() => {
    if (!isNeutralMode) return [];
    return features
      .map((feature) => {
        const precinctId = getFeaturePrecinctId(feature);
        const point = getFeatureRepresentativePoint(feature);
        if (!precinctId || !point) return null;
        return {
          precinctId,
          text: precinctId,
          lat: point.lat,
          lng: point.lng,
        };
      })
      .filter(Boolean);
  }, [features, isNeutralMode]);

  const selectedPrecinctRows = useMemo(() => {
    if (!isNeutralMode || selectedPrecinctIds.size === 0) return [];
    return features
      .map((feature) => {
        const precinctId = getFeaturePrecinctId(feature);
        if (!precinctId || !selectedPrecinctIds.has(precinctId)) return null;
        const voters = hasNeutralPsp2025Stats ? getStatsVoters(statsByPrecinct, precinctId, psp2025ElectionId) : null;
        return {
          precinctId,
          precinctLabel: precinctId,
          voters,
          feature,
        };
      })
      .filter(Boolean)
      .sort((a, b) => comparePrecinctCodes(a.precinctLabel, b.precinctLabel));
  }, [features, hasNeutralPsp2025Stats, isNeutralMode, psp2025ElectionId, selectedPrecinctIds, statsByPrecinct]);
  const addressIndexCacheKey = areaId && dataElectionId ? `${String(areaId)}:${String(dataElectionId)}` : "";
  const activeAddressIndex =
    addressIndexState.cacheKey && addressIndexState.cacheKey === addressIndexCacheKey ? addressIndexState.index : null;
  const activeAddressPoints =
    addressPointsState.cacheKey && addressPointsState.cacheKey === addressIndexCacheKey ? addressPointsState.points : null;
  const isAddressToggleChecked = showAddressSummary && activeAddressIndex?.available !== false;
  const showAddressColumns = showAddressSummary && activeAddressIndex?.available === true;
  const shouldShowAddressUnavailable =
    showAddressSummary &&
    selectedPrecinctRows.length > 0 &&
    ((activeAddressIndex?.available === false && !addressIndexState.isLoading) ||
      (activeAddressPoints?.available === false && !addressPointsState.isLoading));

  useEffect(() => {
    if (!showAddressSummary || !areaId || !dataElectionId || selectedPrecinctRows.length === 0) {
      setAddressIndexState((current) => (current.isLoading ? { cacheKey: "", index: null, isLoading: false } : current));
      return undefined;
    }

    const cacheKey = `${String(areaId)}:${String(dataElectionId)}`;
    const cached = addressIndexCacheRef.current.get(cacheKey);
    if (cached) {
      setAddressIndexState({ cacheKey, index: cached, isLoading: false });
      return undefined;
    }

    const ac = new AbortController();
    setAddressIndexState({ cacheKey, index: null, isLoading: true });

    (async () => {
      try {
        const url = buildAreaArtifactUrl("enrichment/addresses/index", areaId, apiBase, { election_id: dataElectionId });
        const response = await fetch(url, buildApiFetchOptions(apiBase, { signal: ac.signal }));
        if (!response.ok) throw createHttpError(response.status, "address enrichment index");
        const payload = await response.json();
        if (ac.signal.aborted) return;
        const normalized = normalizeAddressEnrichmentIndex(payload);
        addressIndexCacheRef.current.set(cacheKey, normalized);
        setAddressIndexState({ cacheKey, index: normalized, isLoading: false });
      } catch (err) {
        if (err?.name === "AbortError") return;
        const normalized = normalizeAddressEnrichmentIndex({ available: false, reason: "not_available" });
        addressIndexCacheRef.current.set(cacheKey, normalized);
        setAddressIndexState({ cacheKey, index: normalized, isLoading: false });
      }
    })();

    return () => ac.abort();
  }, [areaId, dataElectionId, selectedPrecinctRows.length, showAddressSummary, apiBase]);

  useEffect(() => {
    if (!showAddressColumns || !areaId || !dataElectionId || selectedPrecinctRows.length === 0) {
      setAddressPointsState((current) => (current.isLoading ? { cacheKey: "", points: null, isLoading: false } : current));
      return undefined;
    }

    const cacheKey = `${String(areaId)}:${String(dataElectionId)}`;
    const cached = addressPointsCacheRef.current.get(cacheKey);
    if (cached) {
      setAddressPointsState({ cacheKey, points: cached, isLoading: false });
      return undefined;
    }

    const ac = new AbortController();
    setAddressPointsState({ cacheKey, points: null, isLoading: true });

    (async () => {
      try {
        const url = buildAreaArtifactUrl("enrichment/addresses/points", areaId, apiBase, { election_id: dataElectionId });
        const response = await fetch(url, buildApiFetchOptions(apiBase, { signal: ac.signal }));
        if (!response.ok) throw createHttpError(response.status, "address enrichment points");
        const payload = await response.json();
        if (ac.signal.aborted) return;
        const normalized = normalizeAddressEnrichmentPoints(payload);
        addressPointsCacheRef.current.set(cacheKey, normalized);
        setAddressPointsState({ cacheKey, points: normalized, isLoading: false });
      } catch (err) {
        if (err?.name === "AbortError") return;
        const normalized = normalizeAddressEnrichmentPoints({ available: false, reason: "not_available" });
        addressPointsCacheRef.current.set(cacheKey, normalized);
        setAddressPointsState({ cacheKey, points: normalized, isLoading: false });
      }
    })();

    return () => ac.abort();
  }, [areaId, dataElectionId, selectedPrecinctRows.length, showAddressColumns, apiBase]);

  const selectedPrecinctVotersTotal = useMemo(
    () => selectedPrecinctRows.reduce((sum, row) => (Number.isFinite(row.voters) ? sum + Number(row.voters) : sum), 0),
    [selectedPrecinctRows]
  );
  const selectedAddressTotals = useMemo(() => {
    if (!showAddressColumns) return { streetCount: 0, addressCount: 0 };
    return selectedPrecinctRows.reduce(
      (sum, row) => {
        const addressSummary = getAddressSummaryForSelectedPrecinct(activeAddressIndex, row);
        return {
          streetCount: sum.streetCount + (Number.isFinite(Number(addressSummary?.streetCount)) ? Number(addressSummary.streetCount) : 0),
          addressCount: sum.addressCount + (Number.isFinite(Number(addressSummary?.addressCount)) ? Number(addressSummary.addressCount) : 0),
        };
      },
      { streetCount: 0, addressCount: 0 }
    );
  }, [activeAddressIndex, selectedPrecinctRows, showAddressColumns]);
  const clearSelectedPrecincts = useCallback(() => {
    setSelectedPrecinctIds(new Set());
    setHoveredPrecinct(null);
    setTooltip(null);
  }, []);
  const resetMapToArea = useCallback(() => {
    clearSelectedPrecincts();
    clearStoredViewport("landmarks", areaId);
    setFitRequestKey(makeAutoFitRequestKey("landmarks", areaId, `reset-${electionId || "none"}`));
  }, [areaId, clearSelectedPrecincts, electionId]);

  const activeMhdTypes = useMemo(
    () => LANDMARK_MHD_TYPES.filter((type) => enabledMhdTypes[type]),
    [enabledMhdTypes]
  );
  const markersAllowedByZoom = Number(markerViewport.zoom) >= MARKER_ZOOM_THRESHOLD;
  const hasAreaGeometry = featureCount > 0 && Array.isArray(areaGeometryIndex.polygons) && areaGeometryIndex.polygons.length > 0;
  const visibleMhdMarkers = useMemo(
    () =>
      enabledCategories.mhd && markersAllowedByZoom && hasAreaGeometry
        ? filterMhdStops(mhdStops, {
            enabledTypes: activeMhdTypes,
            geoJsonBounds,
            areaGeometryIndex,
            viewportBounds: markerViewport.bounds,
            maxMarkers: MAX_VISIBLE_MARKERS,
          })
        : [],
    [activeMhdTypes, areaGeometryIndex, enabledCategories.mhd, geoJsonBounds, hasAreaGeometry, markerViewport.bounds, markersAllowedByZoom, mhdStops]
  );
  const activeMhdTotal = useMemo(
    () =>
      enabledCategories.mhd && hasAreaGeometry
        ? filterMhdStops(mhdStops, {
            enabledTypes: activeMhdTypes,
            geoJsonBounds,
            areaGeometryIndex,
            viewportBounds: null,
            maxMarkers: Number.MAX_SAFE_INTEGER,
          }).length
        : 0,
    [activeMhdTypes, areaGeometryIndex, enabledCategories.mhd, geoJsonBounds, hasAreaGeometry, mhdStops]
  );
  const buildNeutralLandmarksReportSnapshot = useCallback(
    () => {
      if (!areaId || !dataElectionId || featureCount < 1) return null;
      return {
        kind: REPORT_ITEM_TYPES.LANDMARKS_MAP,
        sourceModule: REPORT_SOURCE_MODULES.LANDMARKS,
        areaId,
        electionId: dataElectionId,
        electionLabel: NEUTRAL_ELECTION_LABEL,
        neutralMode: true,
        selectedPrecinctIds: selectedPrecinctRows.map((row) => row.precinctId),
        selectedPrecinctRows: selectedPrecinctRows.map((row) => ({
          precinctId: row.precinctId,
          precinctLabel: row.precinctLabel,
          voters: Number.isFinite(row.voters) ? Number(row.voters) : null,
        })),
        selectedSummary: {
          count: selectedPrecinctRows.length,
          votersTotal: selectedPrecinctVotersTotal,
        },
        enabledLayers: { ...enabledCategories },
        enabledMhdTypes: { ...enabledMhdTypes },
        colors: {
          markerColors: { ...markerColors },
        },
        neutralStyle: {
          fillColor: NEUTRAL_FILL_COLOR,
          selectedFillColor: NEUTRAL_SELECTED_FILL_COLOR,
        },
        visibleMarkers: visibleMhdMarkers.slice(0, MAX_REPORT_VISIBLE_MARKERS).map((marker) => ({
          id: String(marker.id || ""),
          type: String(marker.type || ""),
          lat: Number(marker.lat),
          lng: Number(marker.lng),
          color: markerColors[marker.type] || LANDMARK_MHD_COLORS[marker.type] || "",
        })),
        viewport: captureLeafletViewport(mapRef.current) || initialViewport,
        markerViewport,
        capturedAt: new Date().toISOString(),
      };
    },
    [
      areaId,
      dataElectionId,
      enabledCategories,
      enabledMhdTypes,
      featureCount,
      initialViewport,
      markerColors,
      markerViewport,
      selectedPrecinctRows,
      selectedPrecinctVotersTotal,
      visibleMhdMarkers,
    ]
  );

  const loadingText = [
    isLoadingElections ? "volby" : "",
    data.isLoadingDistricts ? "okrsky" : "",
    data.isLoadingResults ? "výsledky" : "",
    data.isLoadingStats ? "statistiky" : "",
    isLoadingMhd ? "MHD" : "",
    isLoadingBillboards ? "billboardy" : "",
  ].filter(Boolean).join(" + ");
  const geometryBlockingError =
    data.districtsError || (!data.isLoadingDistricts && dataElectionId && featureCount === 0 ? "Geometrie okrsků není k dispozici." : "");
  const softWarnings = [electionsError, data.resultsError, data.statsError, areaBoundaryError, mhdError, billboardError].filter(Boolean);
  const mapKey = `landmarks-${areaId || "none"}`;
  const geoJsonKey = `${electionId || "none"}-${featureCount}`;

  return (
    <main className="landmarks-page">
      <style>{`
        .landmarks-page {
          width: 100%;
          min-height: calc(var(--app-shell-available-height, 100vh) - 20px);
          padding: 12px 0 24px;
          box-sizing: border-box;
          color: #0f172a;
          --landmarks-map-height: clamp(360px, calc(var(--app-shell-available-height, 100vh) - 155px), 500px);
        }
        .landmarks-shell {
          width: min(1320px, calc(100% - 36px));
          margin: 0 auto;
          display: grid;
          grid-template-columns: minmax(286px, 330px) minmax(0, 1fr);
          align-items: start;
          gap: 12px;
        }
        .landmarks-panel,
        .landmarks-map-frame {
          border: 1px solid #d7e1eb;
          border-radius: 8px;
          background: #ffffff;
          box-shadow: 0 12px 30px rgba(15, 23, 42, 0.07);
        }
        .landmarks-panel {
          padding: 14px;
          display: grid;
          gap: 14px;
          align-content: start;
          max-height: var(--landmarks-map-height);
          overflow: auto;
        }
        .landmarks-panel-actions {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          flex-wrap: wrap;
          gap: 8px;
        }
        .landmarks-control,
        .landmarks-toggle-block {
          display: grid;
          gap: 6px;
          min-width: 0;
        }
        .landmarks-control > span,
        .landmarks-section-label {
          font-size: 0.74rem;
          font-weight: 800;
          text-transform: uppercase;
          color: #475569;
        }
        .landmarks-control select {
          width: 100%;
          min-height: 34px;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          background: #ffffff;
          color: #0f172a;
          font-size: 0.86rem;
          font-weight: 700;
          padding: 7px 10px;
        }
        .landmarks-control small,
        .landmarks-note {
          color: #64748b;
          font-size: 0.78rem;
          font-weight: 700;
          line-height: 1.35;
        }
        .landmarks-control .is-error,
        .landmarks-status .is-error {
          color: #991b1b;
        }
        .landmarks-toggle-row,
        .landmarks-subtype-row,
        .landmarks-legend-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          min-height: 32px;
        }
        .landmarks-toggle-row,
        .landmarks-subtype-row {
          font-size: 0.88rem;
          font-weight: 800;
        }
        .landmarks-subtype-main {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          min-width: 0;
        }
        .landmarks-subtype-symbol {
          display: inline-grid;
          place-items: center;
          min-width: 24px;
          height: 18px;
          border-radius: 5px;
          color: white;
          font-size: 0.64rem;
          font-weight: 900;
          line-height: 1;
        }
        .landmarks-subtype-actions {
          display: inline-flex;
          align-items: center;
          justify-content: flex-end;
          gap: 7px;
          min-width: 0;
        }
        .landmarks-toggle-row input,
        .landmarks-subtype-row input {
          width: 18px;
          height: 18px;
        }
        .landmarks-placeholder-text {
          padding: 8px 10px;
          border: 1px dashed #cbd5e1;
          border-radius: 8px;
          background: #f8fafc;
          color: #64748b;
          font-size: 0.8rem;
          font-weight: 700;
        }
        .landmarks-status {
          display: grid;
          gap: 6px;
        }
        .landmarks-status span {
          border-radius: 999px;
          padding: 6px 9px;
          background: #eff6ff;
          color: #1e3a8a;
          font-size: 0.78rem;
          font-weight: 800;
        }
        .landmarks-map-frame {
          position: relative;
          min-width: 0;
          height: var(--landmarks-map-height);
          overflow: hidden;
          background: #f8fafc;
        }
        .landmarks-map-frame > div {
          height: 100%;
          border: 0 !important;
          border-radius: 0 !important;
        }
        .landmarks-map-frame .leaflet-top.leaflet-left {
          top: 12px;
          left: 12px;
        }
        .landmarks-map-error {
          position: absolute;
          left: 50%;
          top: 50%;
          z-index: 720;
          transform: translate(-50%, -50%);
          width: min(460px, calc(100vw - 36px));
          padding: 16px;
          text-align: center;
          font-weight: 900;
          color: #991b1b;
          background: rgba(255, 255, 255, 0.94);
          border: 1px solid #fecaca;
          border-radius: 8px;
        }
        .landmarks-map-note {
          position: absolute;
          left: 12px;
          bottom: 28px;
          z-index: 720;
          max-width: min(440px, calc(100% - 24px));
          border: 1px solid rgba(203, 213, 225, 0.9);
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.94);
          padding: 8px 10px;
          color: #334155;
          font-size: 0.78rem;
          font-weight: 800;
          box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
        }
        .landmarks-legend {
          display: grid;
          gap: 7px;
        }
        .landmarks-legend-control-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          min-height: 34px;
        }
        .landmarks-legend-swatch {
          width: 32px;
          height: 14px;
          border-radius: 999px;
          border: 1px solid rgba(15, 23, 42, 0.14);
        }
        .landmarks-legend-text {
          flex: 1 1 auto;
          font-size: 0.8rem;
          font-weight: 800;
          color: #334155;
        }
        .landmarks-tooltip {
          position: fixed;
          z-index: 1800;
          width: max-content;
          min-width: 132px;
          max-width: 220px;
          border: 1px solid rgba(15, 23, 42, 0.14);
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.98);
          box-shadow: 0 18px 40px rgba(15, 23, 42, 0.18);
          padding: 8px 10px;
          pointer-events: none;
        }
        .landmarks-tooltip-title {
          font-weight: 900;
          margin-bottom: 3px;
          color: #0f172a;
          font-size: 0.86rem;
          line-height: 1.2;
        }
        .landmarks-tooltip-subline {
          color: #475569;
          font-size: 0.8rem;
          font-weight: 750;
          line-height: 1.25;
        }
        .landmarks-tooltip-grid {
          display: grid;
          grid-template-columns: 86px minmax(0, 1fr);
          gap: 5px 10px;
          font-size: 0.84rem;
        }
        .landmarks-tooltip-grid span {
          color: #475569;
        }
        .landmarks-marker-tooltip {
          width: max-content;
          min-width: 88px;
          max-width: 190px;
          white-space: normal;
          font-size: 0.78rem;
          font-weight: 700;
          line-height: 1.35;
          overflow-wrap: anywhere;
          word-break: normal;
        }
        .landmarks-marker-tooltip strong,
        .landmarks-marker-tooltip-lines {
          display: block;
          max-width: 170px;
          white-space: normal;
          overflow-wrap: anywhere;
        }
        .landmarks-color-picker {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: 5px;
        }
        .landmarks-color-trigger,
        .landmarks-color-close {
          width: 34px;
          height: 30px;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          background: #ffffff;
          cursor: pointer;
          display: grid;
          place-items: center;
          color: #1e293b;
          font-weight: 900;
        }
        .landmarks-color-button-swatch {
          width: 18px;
          height: 18px;
          border-radius: 5px;
          border: 1px solid rgba(15, 23, 42, 0.18);
        }
        .landmarks-color-menu {
          position: fixed;
          z-index: 2200;
          width: 236px;
          border: 1px solid #d7e1eb;
          border-radius: 8px;
          background: #ffffff;
          box-shadow: 0 18px 38px rgba(15, 23, 42, 0.18);
          padding: 12px;
          display: grid;
          gap: 12px;
        }
        .landmarks-color-menu-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }
        .landmarks-color-menu-title {
          font-size: 0.74rem;
          font-weight: 800;
          color: #334155;
        }
        .landmarks-color-close {
          width: 28px;
          height: 28px;
          font-size: 1rem;
        }
        .landmarks-color-grid {
          display: grid;
          grid-template-columns: repeat(6, 26px);
          gap: 8px;
        }
        .landmarks-color-preset {
          width: 26px;
          height: 26px;
          border: 1px solid rgba(15, 23, 42, 0.12);
          border-radius: 6px;
          cursor: pointer;
        }
        .landmarks-color-preset.is-active {
          outline: 2px solid ${BRAND_COLORS.primaryDarkBlue};
          outline-offset: 2px;
        }
        .landmarks-div-icon {
          background: transparent;
          border: 0;
        }
        .landmarks-marker-label {
          display: inline-grid;
          place-items: center;
          min-width: 16px;
          height: 14px;
          padding: 0 3px;
          border: 1px solid rgba(255, 255, 255, 0.86);
          border-radius: 4px;
          background: var(--landmark-marker-color);
          color: white;
          box-shadow: 0 1px 4px rgba(15, 23, 42, 0.22);
          font-size: 9px;
          font-weight: 900;
          line-height: 1;
          opacity: 0.78;
          transform: translate(-50%, -50%);
          pointer-events: auto;
        }
        .landmarks-marker-label:hover {
          opacity: 0.94;
        }
        .landmarks-precinct-label-icon {
          background: transparent;
          border: 0;
          pointer-events: none;
        }
        .landmarks-precinct-label {
          display: inline-grid;
          place-items: center;
          min-width: 34px;
          max-width: 58px;
          height: 18px;
          padding: 0 5px;
          border: 1px solid rgba(100, 116, 139, 0.28);
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.72);
          color: #334155;
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
          font-size: 10px;
          font-weight: 900;
          line-height: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          pointer-events: none;
        }
        .landmarks-data-section {
          width: min(1480px, calc(100% - 20px));
          margin: 12px auto 0;
          border: 1px solid #d7e1eb;
          border-radius: 8px;
          background: #ffffff;
          box-shadow: 0 12px 30px rgba(15, 23, 42, 0.07);
          padding: 14px;
          box-sizing: border-box;
        }
        .landmarks-data-header {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 12px;
        }
        .landmarks-data-title {
          display: inline-flex;
          align-items: baseline;
          gap: 12px;
          min-width: 0;
        }
        .landmarks-data-header-actions {
          display: inline-flex;
          align-items: center;
          justify-content: flex-end;
          flex-wrap: wrap;
          gap: 12px;
        }
        .landmarks-address-toggle {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          color: #334155;
          font-size: 0.82rem;
          font-weight: 850;
        }
        .landmarks-address-toggle input {
          width: 17px;
          height: 17px;
        }
        .landmarks-data-header h2 {
          margin: 0;
          color: #0f172a;
          font-size: 1.08rem;
          line-height: 1.2;
        }
        .landmarks-data-clear-button {
          min-height: 28px;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          background: #ffffff;
          color: #1e293b;
          cursor: pointer;
          font-size: 0.76rem;
          font-weight: 850;
          padding: 5px 9px;
        }
        .landmarks-data-clear-button:hover:not(:disabled) {
          border-color: ${BRAND_COLORS.primaryBrightBlue};
          color: ${BRAND_COLORS.primaryDarkBlue};
        }
        .landmarks-data-clear-button:disabled {
          cursor: default;
          opacity: 0.48;
        }
        .landmarks-data-empty {
          margin: 0;
          color: #64748b;
          font-size: 0.88rem;
          font-weight: 700;
        }
        .landmarks-address-unavailable {
          margin: -2px 0 10px;
          color: #64748b;
          font-size: 0.8rem;
          font-weight: 750;
        }
        .landmarks-data-table-wrap {
          overflow-x: auto;
        }
        .landmarks-data-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.88rem;
        }
        .landmarks-data-table th,
        .landmarks-data-table td {
          padding: 9px 10px;
          border-bottom: 1px solid #e2e8f0;
          text-align: left;
        }
        .landmarks-data-table th {
          color: #475569;
          font-size: 0.74rem;
          text-transform: uppercase;
          letter-spacing: 0;
        }
        .landmarks-data-table td {
          color: #0f172a;
          font-weight: 750;
        }
        .landmarks-address-list-cell {
          min-width: min(520px, 54vw);
          max-width: 680px;
        }
        .landmarks-address-list {
          max-height: 128px;
          overflow: auto;
          color: #334155;
          font-size: 0.76rem;
          font-weight: 720;
          line-height: 1.45;
          overflow-wrap: anywhere;
        }
        .landmarks-address-street-line + .landmarks-address-street-line {
          margin-top: 2px;
        }
        .landmarks-address-street-name {
          color: #0f172a;
          font-weight: 900;
        }
        .landmarks-address-list-empty {
          color: #64748b;
          font-size: 0.78rem;
          font-weight: 760;
        }
        .landmarks-data-summary {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 12px;
        }
        .landmarks-data-summary span {
          border-radius: 999px;
          background: #eff6ff;
          color: #1e3a8a;
          padding: 7px 10px;
          font-size: 0.8rem;
          font-weight: 900;
        }
        @media (max-width: 960px) {
          .landmarks-shell {
            grid-template-columns: minmax(0, 1fr);
          }
          .landmarks-panel {
            max-height: none;
          }
          .landmarks-map-frame {
            height: clamp(340px, calc(var(--app-shell-available-height, 100vh) - 170px), 480px);
          }
        }
      `}</style>
      <section className="landmarks-shell" aria-label="Místa">
        <aside className="landmarks-panel">
          <label className="landmarks-control">
            <span>Volby</span>
            <select value={electionId} onChange={(event) => setElectionId(event.target.value)} disabled={isLoadingElections || !areaId}>
              <option value={NEUTRAL_ELECTION_ID}>{NEUTRAL_ELECTION_LABEL}</option>
              {elections.map((election) => (
                <option key={String(election.id)} value={String(election.id)}>
                  {getElectionLabel(election)}
                </option>
              ))}
            </select>
          </label>
          {!isNeutralMode ? (
            <label className="landmarks-control">
              <span>{subjectLabel}</span>
              <select value={selectedEntity} onChange={(event) => setSelectedEntity(event.target.value)} disabled={parties.length === 0}>
                {parties.length === 0 ? <option value="">Bez dostupných entit</option> : null}
                {parties.map((party) => (
                  <option key={party} value={party}>
                    {party}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <div className="landmarks-toggle-block">
            <div className="landmarks-section-label">Vrstvy</div>
            {VISIBLE_LANDMARK_CATEGORIES.map((category) => (
              <label key={category.key} className="landmarks-toggle-row">
                <span>{category.label}</span>
                <input
                  type="checkbox"
                  checked={Boolean(enabledCategories[category.key])}
                  onChange={(event) =>
                    setEnabledCategories((current) => ({
                      ...current,
                      [category.key]: event.target.checked,
                    }))
                  }
                />
              </label>
            ))}
          </div>

          {enabledCategories.mhd ? (
            <div className="landmarks-toggle-block">
              <div className="landmarks-section-label">MHD - druh</div>
              {LANDMARK_MHD_TYPES.map((type) => (
                <div key={type} className="landmarks-subtype-row">
                  <span className="landmarks-subtype-main">
                    <span
                      className="landmarks-subtype-symbol"
                      style={{ background: markerColors[type] || LANDMARK_MHD_COLORS[type] }}
                      aria-hidden="true"
                    >
                      {LANDMARK_MHD_TYPE_MARKERS[type]}
                    </span>
                    <span>{LANDMARK_MHD_TYPE_LABELS[type]}</span>
                  </span>
                  <span className="landmarks-subtype-actions">
                    <LandmarksColorPresetPicker
                      pickerKey={`mhd-${type}`}
                      label={LANDMARK_MHD_TYPE_LABELS[type]}
                      value={markerColors[type] || LANDMARK_MHD_COLORS[type]}
                      colorPresets={MARKER_COLOR_OPTIONS}
                      openPicker={openPicker}
                      setOpenPicker={setOpenPicker}
                      onChange={(color) =>
                        setMarkerColors((current) => ({
                          ...current,
                          [type]: color,
                        }))
                      }
                    />
                    <input
                      type="checkbox"
                      aria-label={`Zobrazit ${LANDMARK_MHD_TYPE_LABELS[type]}`}
                      checked={Boolean(enabledMhdTypes[type])}
                      onChange={(event) =>
                        setEnabledMhdTypes((current) => ({
                          ...current,
                          [type]: event.target.checked,
                        }))
                      }
                    />
                  </span>
                </div>
              ))}
            </div>
          ) : null}

          {enableReports ? (
            <div className="landmarks-panel-actions">
              <AddToReportButton
                type={REPORT_ITEM_TYPES.LANDMARKS_MAP}
                sourceModule={REPORT_SOURCE_MODULES.LANDMARKS}
                defaultOrientation={REPORT_ORIENTATIONS.LANDSCAPE}
                defaultTitle={
                  isNeutralMode
                    ? buildDefaultReportTitle([NEUTRAL_ELECTION_LABEL, "Místa"])
                    : buildDefaultReportTitle([electionLabel, selectedEntity || "Místa", "Místa"])
                }
                buildSnapshot={isNeutralMode ? buildNeutralLandmarksReportSnapshot : buildLandmarksReportSnapshot}
              />
            </div>
          ) : null}

          {!isNeutralMode ? (
          <div className="landmarks-legend">
            <div className="landmarks-section-label">Legenda</div>
            <div className="landmarks-legend-control-row">
              <span className="landmarks-legend-text">Barva škály</span>
              <LandmarksColorPresetPicker
                pickerKey="scale"
                label="Barva škály"
                value={baseColor}
                colorPresets={MARKER_COLOR_OPTIONS}
                openPicker={openPicker}
                setOpenPicker={setOpenPicker}
                onChange={setBaseColor}
              />
            </div>
            {paletteSteps.map((color, index) => (
              <div key={`${color}-${index}`} className="landmarks-legend-row">
                <span className="landmarks-legend-swatch" style={{ background: color }} aria-hidden="true" />
                <span className="landmarks-legend-text">{formatBinRange(bins[index], bins[index + 1])}</span>
              </div>
            ))}
            <div className="landmarks-legend-row">
              <span className="landmarks-legend-swatch" style={{ background: NO_DATA_FILL_COLOR }} aria-hidden="true" />
              <span className="landmarks-legend-text">bez dat pro výběr</span>
            </div>
          </div>
          ) : null}

          {loadingText || softWarnings.length > 0 ? (
            <div className="landmarks-status" role={softWarnings.length > 0 ? "alert" : "status"}>
              {loadingText ? <span>Načítám: {loadingText}</span> : null}
              {softWarnings.map((warning) => (
                <span key={warning} className="is-error">{warning}</span>
              ))}
            </div>
          ) : null}
        </aside>

        <section className="landmarks-map-frame" aria-label="Volební mapa s orientačními body">
          <MapPane
            mapKey={mapKey}
            geoJsonKey={geoJsonKey}
            districtsGeoJson={data.districtsGeoJson || EMPTY_GEOJSON}
            styleFeature={styleFeature}
            onEachFeature={onEachFeature}
            mapStandard={ELECTION_MAP_STANDARD_V1_KEY}
            height="100%"
            scrollWheelZoom={false}
            initialView={initialViewport}
            fitGeoJsonBounds
            fitBoundsRequestKey={fitRequestKey}
            fitBoundsPadding={[28, 28]}
            fitBoundsMaxZoom={14}
            onMapReady={(map) => {
              mapRef.current = map;
            }}
            onViewChange={(center, zoom) => {
              writeStoredViewport("landmarks", areaId, { center, zoom });
            }}
            onMouseLeave={() => {
              setHoveredPrecinct(null);
              setTooltip(null);
            }}
            onInteractionStart={() => {
              setHoveredPrecinct(null);
              setTooltip(null);
            }}
            onResetView={resetMapToArea}
          >
            <LandmarkMarkersLayer markers={visibleMhdMarkers} markerColors={markerColors} onViewportChange={setMarkerViewport} />
            {isNeutralMode ? <PrecinctNumberLabelsLayer labels={precinctLabels} /> : null}
          </MapPane>
          {geometryBlockingError ? <div className="landmarks-map-error">{geometryBlockingError}</div> : null}
          {!geometryBlockingError && enabledCategories.mhd && !markersAllowedByZoom ? (
            <div className="landmarks-map-note">Přibližte mapu pro zobrazení MHD bodů.</div>
          ) : null}
          {!geometryBlockingError && enabledCategories.mhd && markersAllowedByZoom && activeMhdTotal > MAX_VISIBLE_MARKERS ? (
            <div className="landmarks-map-note">Zobrazuji prvních {formatInt(visibleMhdMarkers.length)} MHD bodů v aktuálním výřezu. Přibližte mapu pro menší hustotu.</div>
          ) : null}
        </section>
      </section>

      {isNeutralMode ? (
        <section className="landmarks-data-section" aria-label="Data vybraných okrsků">
          <div className="landmarks-data-header">
            <div className="landmarks-data-title">
              <h2>Data</h2>
              <button
                type="button"
                className="landmarks-data-clear-button"
                onClick={clearSelectedPrecincts}
                disabled={selectedPrecinctRows.length === 0}
              >
                Vymazat
              </button>
            </div>
            <div className="landmarks-data-header-actions">
              <label className="landmarks-address-toggle">
                <span>Adresy</span>
                <input
                  type="checkbox"
                  checked={isAddressToggleChecked}
                  onChange={(event) => setShowAddressSummary(event.target.checked)}
                />
              </label>
              <span className="landmarks-section-label">Vybrané okrsky</span>
            </div>
          </div>
          {selectedPrecinctRows.length === 0 ? (
            <p className="landmarks-data-empty">Kliknutím do mapy vyberte okrsky.</p>
          ) : (
            <>
              {shouldShowAddressUnavailable ? <p className="landmarks-address-unavailable">Adresní data nejsou k dispozici</p> : null}
              <div className="landmarks-data-table-wrap">
                <table className="landmarks-data-table">
                  <thead>
                    <tr>
                      <th>Č. okrsku</th>
                      <th>Počet voličů (2025)</th>
                      {showAddressColumns ? <th>Ulic</th> : null}
                      {showAddressColumns ? <th>Vchodů</th> : null}
                      {showAddressColumns ? <th>Seznam adres</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPrecinctRows.map((row) => {
                      const addressSummary = showAddressColumns ? getAddressSummaryForSelectedPrecinct(activeAddressIndex, row) : null;
                      const addressGroups = showAddressColumns
                        ? groupAddressPointsForDisplay(getAddressPointsForSelectedPrecinct(activeAddressPoints, row, activeAddressIndex))
                        : [];
                      return (
                        <tr key={row.precinctId}>
                          <td>{row.precinctLabel}</td>
                          <td>{row.voters == null ? "–" : formatInt(row.voters)}</td>
                          {showAddressColumns ? <td>{addressSummary ? formatInt(addressSummary.streetCount) : "–"}</td> : null}
                          {showAddressColumns ? <td>{addressSummary ? formatInt(addressSummary.addressCount) : "–"}</td> : null}
                          {showAddressColumns ? (
                            <td className="landmarks-address-list-cell">
                              {addressGroups.length ? (
                                <div className="landmarks-address-list">
                                  {addressGroups.map((group) => (
                                    <div className="landmarks-address-street-line" key={group.street}>
                                      <span className="landmarks-address-street-name">{group.street}</span>: {group.numbers.join(" · ")}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="landmarks-address-list-empty">–</span>
                              )}
                            </td>
                          ) : null}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <div className="landmarks-data-summary">
            <span>Počet vybraných okrsků: {formatInt(selectedPrecinctRows.length)}</span>
            <span>Celkem voličů: {formatInt(selectedPrecinctVotersTotal)}</span>
            {showAddressColumns ? <span>Počet ulic: {formatInt(selectedAddressTotals.streetCount)}</span> : null}
            {showAddressColumns ? <span>Celkem vchodů: {formatInt(selectedAddressTotals.addressCount)}</span> : null}
          </div>
        </section>
      ) : null}

      {tooltip && tooltipRow ? (
        <div className="landmarks-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
          <div className="landmarks-tooltip-title">{tooltipRow.precinctLabel}</div>
          {isNeutralMode ? (
            <div className="landmarks-tooltip-subline">{tooltipRow.voters2025 == null ? "–" : formatInt(tooltipRow.voters2025)} voličů (2025)</div>
          ) : (
            <div className="landmarks-tooltip-grid">
              <span>{subjectLabel}</span>
              <strong>{selectedEntity || "Bez výběru"}</strong>
              <span>Hlasy</span>
              <strong>{tooltipRow.votes == null ? "–" : formatInt(tooltipRow.votes)}</strong>
              <span>Podíl</span>
              <strong>{tooltipRow.percentage == null ? "–" : formatPct(tooltipRow.percentage)}</strong>
              <span>Účast</span>
              <strong>{tooltipRow.statsInfo?.turnoutPct == null ? "–" : formatPct(tooltipRow.statsInfo.turnoutPct)}</strong>
            </div>
          )}
        </div>
      ) : null}
    </main>
  );
}
